import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './Book.module.css';

import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { useSlice } from '../../hooks/useSlice';
import useHover from '../../hooks/useHover';
import { usePage } from '../../hooks/usePage';

import { selectMenu } from '../../features/infoSlice';
import { libApiSlice, selectProjectById } from '../../services/library';
import { shareApiSlice, selectSharedById } from '../../services/share';
import { driveApiSlice, selectResourceById } from '../../services/drive';
import { searchApiSlice, selectPublicById } from '../../services/search';
import { trashApiSlice, selectTrashById } from '../../services/trash';
import { useCountHitsMutation } from '../../services/common';

import { openSelpubCloud } from '../../utils/selpub';
import { formatDate, getDate, now } from '../../utils/date';

import Buttons from './Buttons';
import Select from './Select';
import {
  SHARING_ENDED,
  SHARED_EBOOK,
  NEW_ENG,
  SHARING,
  FOLDER,
} from '../../constants';
import { getThumbnail } from '../../utils/image';
import {
  download,
  formatBytes,
  getFileType,
  isMobile,
  openNewWindow,
} from '../../utils/util';
import { Options } from '../../types/library';
import { useNavigate } from 'react-router-dom';

interface Book {
  name?: string;
  entity?: any;
  item: any;
  classnames?: string[];
}

export default function Book({ name, entity, item, classnames }: Book) {
  const dispatch = useAppDispatch();
  const cx = classNames.bind(styles);
  const menu = useAppSelector(selectMenu);
  const navigate = useNavigate();

  const [getLibTrigger] = libApiSlice.useLazyGetLibProjectQuery();
  const [getSharedTrigger] = shareApiSlice.useLazyGetSharedProjectQuery();
  const [getResourceTrigger] = driveApiSlice.useLazyGetResourceQuery();
  const [getPublicTrigger] = searchApiSlice.useLazyGetPublicProjectQuery();
  const [getTrashTrigger] = trashApiSlice.useLazyGetTrashQuery();
  const [countHits] = useCountHitsMutation();

  const getDetailTrigger = {
    library: getLibTrigger,
    share: getSharedTrigger,
    mydrive: getResourceTrigger,
    search: getPublicTrigger,
    trash: getTrashTrigger,
  }[menu];

  const slice = useSlice();
  const { detailsToggled, itemSelected, selectSelectedIds, selectOptions } =
    slice;

  const {
    layoutType,
    isEditMode,
    isDetailsOpened,
    sortType,
    sortOrder,
  }: Options = useAppSelector(selectOptions);
  const selectedIds = useAppSelector(selectSelectedIds);
  const [isHover, hoverEventHandlers] = useHover();
  const { initPageList } = usePage();

  const isMain = menu === 'main';
  const isTrash = menu === 'trash';
  const isGrid = !isMain && layoutType === 'grid';
  const isList = !isMain && layoutType === 'list';

  const [id, title, fileName] = [
    item.projectId || item.resourceId || item.seq,
    item.title || item.orgFileName || item.name,
    item.orgFileName ?? '',
  ];

  const byIdSelectors: any = {
    main: selectProjectById,
    library: selectProjectById,
    share: selectSharedById,
    mydrive: selectResourceById,
    search: selectPublicById,
    trash: selectTrashById,
    settings: selectProjectById,
  };

  const curItem = useAppSelector((state) =>
    byIdSelectors[name ?? menu](state, id)
  );
  const resource = curItem?.projectResource || curItem?.resource;
  const ext = resource?.ext;
  const regDate = resource?.regDate || resource?.dispRegDate;
  const isSharedLibrary = menu === 'share';
  const sharedDate = isSharedLibrary ? curItem?.projectShare?.shareDate : '';
  const formattedRegDate = regDate ? formatDate(regDate) : '';
  const size = resource?.size;
  const hits = curItem?.project?.hit;
  // TODO: 소유자 API 연동 필요
  const owner = isSharedLibrary
    ? curItem?.projectShare?.owner
    : isMain
      ? /* item.owner */ 'Owner'
      : curItem?.project?.owner;

  const sharedExpDate = curItem?.projectShare?.expDate;
  const isEndSharing = isMain ? item.isExp : curItem?.isExp;
  const dDay = useMemo(
    () =>
      isMain
        ? item.expDays
        : sharedExpDate
          ? Math.ceil(getDate(sharedExpDate).diff(now, 'd', true))
          : -1,
    [sharedExpDate]
  );

  const isSharing = isMain
    ? item.isShare
    : (curItem?.isShare ?? !!curItem?.share?.link);
  const isNew = isMain ? item.isNew : curItem?.isNew;
  const isShowDday = useMemo(
    () => !!dDay && dDay >= 0 && dDay <= 7,
    [sharedExpDate]
  );
  const typeIcoClass = `ico_file_${getFileType(ext)}`;
  const isSearch = menu === 'search';
  const isShared = curItem?.isShare;
  const isFolder = ext === 'folder';
  const isSelectedDetailsOpened = isDetailsOpened && selectedIds?.includes(id);
  const isHoverButtons =
    isGrid && isHover && !isMobile && !isSelectedDetailsOpened;

  const thumbnail =
    item?.coverFilePath ||
    getThumbnail(
      isFolder && isSelectedDetailsOpened ? 'folder_using' : item.ext
    );

  const [isShowRestoreButton, setIsShowRestoreButton] = useState(false);

  const clickCover = async (e: React.MouseEvent) => {
    if (isMobile) {
      e.stopPropagation();
    }

    menu === 'trash'
      ? setIsShowRestoreButton(!isShowRestoreButton)
      : name !== 'mydrive' && menu !== 'mydrive'
        ? (window.open('', 'selpub_viewer'),
          await countHits(id).unwrap(),
          openSelpubCloud('viewer', id, false, '', false, name),
          setTimeout(
            () =>
              dispatch(
                initPageList({ page: 0, sortBy: sortType, sortDir: sortOrder })
              ),
            3000
          ))
        : ext === 'web'
          ? openNewWindow(curItem.resource.url)
          : ext === 'folder'
            ? navigate(`folders/${curItem?.resource?.folderId}`, {
                state: {
                  folderId: curItem?.resource?.folderId,
                  folderName: curItem?.resource?.orgFileName,
                },
              })
            : download(id);
  };

  const toggleDetails = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isSelectedDetailsOpened) {
      dispatch(detailsToggled());
    } else {
      getDetailTrigger &&
        getDetailTrigger(id)
          .unwrap()
          .then((res: any) => {
            const item = {
              ...(curItem || {}),
              ...res.data,
            };
            dispatch(
              itemSelected({
                ids: [id],
                item,
              })
            );
            !isDetailsOpened && dispatch(detailsToggled());
          });
    }
  };

  useEffect(() => {
    // dispatch(itemSelected({ ids: [id], item: curItem }));
  }, [curItem]);

  return (
    <div
      className={cx(
        'book_items',
        { [layoutType]: !isMain },
        classnames,
        {
          active: !isEditMode && isSelectedDetailsOpened,
        },
        name ?? menu,
        { folder: isFolder }
      )}
      {...(isGrid && { onClick: toggleDetails })}
    >
      {isList && (
        <span className={cx('binfo_type')}>
          {isFolder ? (
            <>
              <span className={cx('txt')}>-</span>
              <span className={cx('blind')}>{FOLDER}</span>
            </>
          ) : (
            <span className={cx(typeIcoClass)}>{ext}</span>
          )}
        </span>
      )}
      <div>
        <div
          className={cx('cover_area', { end_sharing: isEndSharing })}
          title={title}
          {...(!isHoverButtons &&
            !isSelectedDetailsOpened &&
            (isMain || layoutType === 'grid') &&
            !isEndSharing && { onClick: clickCover })}
          {...hoverEventHandlers}
        >
          <div className={cx('thum_box')}>
            <img src={thumbnail} alt={title} loading="lazy" />
            <span className={cx('iconbox')}>
              {isNew && <span className={cx('marker', 'new')}>{NEW_ENG}</span>}
              {isSharing && (
                <span className={cx('marker', 'share')}>{SHARING}</span>
              )}
              {(isMain || isSharedLibrary) && isShowDday && (
                <span className={cx('marker', 'dday')}>D-{dDay}</span>
              )}
              {isEndSharing && (
                <span className={cx('marker', 'end')}>
                  <span>{SHARING_ENDED}</span>
                </span>
              )}
              {isSearch && (
                <>
                  {isShared && (
                    <span className={cx('marker', 'register')}>
                      {SHARED_EBOOK}
                    </span>
                  )}
                  {!isList && (
                    <span className={cx('marker', 'view')}>{hits ?? 0}</span>
                  )}
                </>
              )}
            </span>
          </div>
          {((isHoverButtons && !isEditMode && !isEndSharing && !isMobile) ||
            (isMobile && isShowRestoreButton)) && (
            <Buttons id={id} entity={entity} item={item} isShared={isShared} />
          )}
        </div>
      </div>

      <h3 className={cx('binfo_title')} title={title}>
        {title}
      </h3>
      {(menu === 'mydrive' || isTrash) && (
        <p className={cx('binfo_size', 'binfo_date')}>
          {!isFolder && size ? formatBytes(size) : isList ? '-' : ''}
        </p>
      )}
      {(isSharedLibrary || (isMain && name === 'share')) && (
        <p className={cx('binfo_date', 'author_nickname')}>{owner}</p>
      )}
      {isSearch && (
        <>
          {isList ? (
            <>
              <p className={cx('binfo_date', 'hits')}>{hits ?? 0}</p>
              <p className={cx('binfo_date', 'author_nickname')}>{owner}</p>
            </>
          ) : (
            <div className={cx('binfo_info_box')}>
              <p className={cx('binfo_date', 'author_nickname')}>{owner}</p>
              <p className={cx('binfo_date')}>{formattedRegDate}</p>
            </div>
          )}
        </>
      )}
      {isList && (
        <>
          <p className={cx('binfo_date')}>
            {isTrash
              ? formatDate(resource?.deleteDate)
              : isSharedLibrary
                ? formatDate(sharedDate)
                : formattedRegDate}
          </p>
          {!isEditMode && <Buttons id={id} entity={entity} item={item} />}
        </>
      )}

      {!isMain && isEditMode && layoutType && !isFolder && (
        <Select type={layoutType} projectId={id} />
      )}
    </div>
  );
}
